/**
 * Login Page Component
 *
 * The main login page that renders the login form within a full-page layout.
 * Handles navigation and provides context for the authentication flow.
 */

import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Bot, AlertCircle } from 'lucide-react'

import { LoginForm } from '../../components/forms/LoginForm'
import { logger, loggerUtils } from '../../utils/logger'
import { getUserFriendlyErrorMessage } from '../../utils/errorMessages'

/**
 * LoginPage Component
 *
 * Full-page layout for user authentication
 */
export const LoginPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [oauthError, setOauthError] = React.useState<string | null>(null)

  // ====================================
  // LIFECYCLE & LOGGING
  // ====================================

  React.useEffect(() => {
    // Check for OAuth error in URL
    const errorParam = searchParams.get('error')
    if (errorParam) {
      const friendlyError = getUserFriendlyErrorMessage(errorParam)
      setOauthError(friendlyError)
      logger.error('OAuth error on login page', { error: errorParam, friendlyError })

      // Clear error from URL after reading it
      searchParams.delete('error')
      setSearchParams(searchParams, { replace: true })
    }

    // Log page visit for analytics
    logger.info('Login page loaded')
    loggerUtils.userAction('page_visit', 'login_page')

    // Set page title
    document.title = 'Sign In - CleverAct AI'

    return () => {
      logger.component('LoginPage', 'unmount')
    }
  }, [])

  // ====================================
  // EVENT HANDLERS
  // ====================================

  /**
   * Handle successful login
   */
  const handleLoginSuccess = () => {
    logger.auth('Login success - redirecting to dashboard')
    loggerUtils.userAction('login_success', 'login_page')
  }

  /**
   * Handle login error
   */
  const handleLoginError = (error: string) => {
    logger.error('Login error on page', {
      component: 'LoginPage',
      error,
      action: 'login_attempt'
    })
  }

  /**
   * Handle navigation to home page
   */
  const handleNavigateHome = () => {
    loggerUtils.userAction('navigate_to_home', 'home_link')
  }

  // ====================================
  // RENDER
  // ====================================

  return (
    <div className="min-h-screen bg-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Header with Logo */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link
          to="/"
          className="flex items-center justify-center space-x-2 group"
          onClick={handleNavigateHome}
        >
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary to-emerald-500 p-[2px]">
            <div className="flex h-full w-full items-center justify-center rounded-[6px] bg-background">
              <Bot className="h-6 w-6 text-primary" />
            </div>
          </div>
          <span className="text-2xl font-bold text-foreground group-hover:text-foreground/80 transition-colors">
            CleverAct AI
          </span>
        </Link>

        <p className="mt-2 text-center text-sm text-muted-foreground">
          Build AI agents that actually do things
        </p>
      </div>

      {/* OAuth Error Alert */}
      {oauthError && (
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start">
            <AlertCircle className="h-5 w-5 text-destructive mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-destructive mb-1">
                Authentication Failed
              </h3>
              <p className="text-sm text-destructive/80">
                {oauthError}
              </p>
            </div>
            <button
              onClick={() => setOauthError(null)}
              className="ml-3 text-destructive hover:text-destructive/80 focus:outline-none"
              aria-label="Dismiss"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Login Form */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <LoginForm
          onSuccess={handleLoginSuccess}
          onError={handleLoginError}
          className="shadow-lg"
        />
      </div>

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-xs text-muted-foreground">
          By signing in, you agree to our{' '}
          <Link
            to="/terms"
            className="text-primary hover:text-primary/80 underline focus:outline-none"
            onClick={() => loggerUtils.userAction('view_terms', 'terms_link')}
          >
            Terms of Service
          </Link>
          {' '}and{' '}
          <Link
            to="/privacy"
            className="text-primary hover:text-primary/80 underline focus:outline-none"
            onClick={() => loggerUtils.userAction('view_privacy', 'privacy_link')}
          >
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  )
}

export default LoginPage
